import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Phone, ArrowLeft, MessageCircle, Menu, X } from 'lucide-react';
import { authService } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import PhoneInput from '../components/common/PhoneInput';

const CustomerLogin: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshUser, handleLoginSuccess } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('965');
  const [phoneNumberOnly, setPhoneNumberOnly] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Clear any existing auth state before attempting new login
    authService.clearAuthData();
    refreshUser(); // Update auth context to reflect cleared state

    try {
      const fullPhoneNumber = `+${countryCode}${phoneNumberOnly}`;
      await authService.customerLogin(fullPhoneNumber);
      setPhoneNumber(fullPhoneNumber); // Update for OTP verification
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Clear any existing auth state before attempting new login verification
    authService.clearAuthData();
    refreshUser(); // Update auth context to reflect cleared state

    try {
      const response = await authService.verifyCustomerOTP(phoneNumber, otp);
      
      // The auth service already stores the tokens in storeAuthData method
      // Update auth context immediately with user data
      handleLoginSuccess(response.user);
      
      console.log('Customer login successful, navigating to dashboard');
      
      // Small delay to ensure context update completes before navigation
      setTimeout(() => {
        console.log('CustomerLogin: About to navigate to /dashboard');
        // Force navigation using window.location if React Router fails
        try {
          navigate('/dashboard', { replace: true });
          console.log('CustomerLogin: navigate() called successfully');
          
          // Fallback: If navigation doesn't work after 500ms, use window.location
          setTimeout(() => {
            console.log('CustomerLogin: Checking if still on login page...');
            if (window.location.pathname.includes('login')) {
              console.log('CustomerLogin: Still on login page, using window.location');
              const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                               window.location.pathname.startsWith('/dev') ? '/dev' : '';
              window.location.href = basename + '/dashboard';
            }
          }, 500);
        } catch (navError) {
          console.error('CustomerLogin: Navigation error:', navError);
          const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                           window.location.pathname.startsWith('/dev') ? '/dev' : '';
          window.location.href = basename + '/dashboard';
        }
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.features')}
              </Link>
              <Link to="/#about" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.about')}
              </Link>
              <Link to="/pricing" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.pricing')}
              </Link>
              </div>

              <LanguageToggle />

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <Link 
                to="/#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-64px)]">
        <div className="max-w-md w-full">
          {/* Back Button */}
          <button
            onClick={() => navigate('/login')}
            className="mb-8 flex items-center text-gray-600 hover:text-[#008B8B] transition"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('login.backToHome')}
          </button>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <div className="bg-[#FF8C00] bg-opacity-10 p-4 rounded-full inline-block mb-4">
                <Car className="w-12 h-12" style={{ color: '#FF8C00' }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">{t('customerLogin.title')}</h2>
              <p className="text-gray-600 mt-2">
                {t('customerLogin.subtitle')}
              </p>
            </div>

            {step === 'phone' ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                    {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
                  </div>
                )}

                <div>
                  <PhoneInput
                    label={t('customerLogin.phoneNumber')}
                    countryCode={countryCode}
                    phoneNumber={phoneNumberOnly}
                    onCountryCodeChange={setCountryCode}
                    onPhoneNumberChange={setPhoneNumberOnly}
                    placeholder="5123 4567"
                    required
                    disabled={loading}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {t('customerLogin.phoneHint')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || !phoneNumberOnly.trim()}
                  className="w-full py-2 px-4 rounded-lg font-medium text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: '#FF8C00' }}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#E67E00')}
                  onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#FF8C00')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {loading ? t('customerLogin.sendingOTP') : t('customerLogin.sendOTP')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                    {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
                  </div>
                )}

                <div className="text-center mb-6">
                  <p className="text-gray-600">
                    {t('customerLogin.otpSent')}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{phoneNumber}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customerLogin.enterOTP')}
                  </label>
                  <input
                    type="text"
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-[#FF8C00] text-center text-lg font-mono"
                    placeholder="123456"
                    maxLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-4 rounded-lg font-medium text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#FF8C00' }}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#E67E00')}
                  onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#FF8C00')}
                >
                  {loading ? t('customerLogin.verifying') : t('customerLogin.verify')}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                    setError('');
                  }}
                  className="w-full text-gray-600 hover:text-[#008B8B] text-sm"
                >
                  {t('customerLogin.changeNumber')}
                </button>
              </form>
            )}

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700 text-center">
                <strong>{t('customerLogin.note')}</strong> {t('customerLogin.noteText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;